import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calculator, TrendingUp, Info } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcCalculators, bcCalculatorGet, bcCalculatorCompute } from "@/lib/api";

export function BusinessCenterCalculators() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    document.title = "ROI Calculators | WebsiteTech.pro";
    bcCalculators().then((d) => setItems(d.items || []));
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-calcs">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">ROI Calculators</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">Transparent calculators. Every formula is visible. Every assumption is editable. Three scenarios (conservative / expected / optimistic) so you can compare.</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((c) => (
              <Link key={c.kind} to={`/business-center/calculators/${c.kind}`} data-testid={`bc-calc-${c.kind}`}
                    className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-indigo-400 hover:shadow-md transition">
                <Calculator className="w-6 h-6 text-indigo-600" />
                <div className="mt-4 font-bold text-slate-900">{c.label}</div>
                <div className="mt-1 text-sm text-slate-600">{c.description}</div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function BusinessCenterCalculatorDetail() {
  const { kind } = useParams();
  const [meta, setMeta] = useState(null);
  const [vals, setVals] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bcCalculatorGet(kind).then((d) => {
      setMeta(d);
      const init = {};
      (d.assumptions || []).forEach((a) => { init[a.key] = a.default; });
      setVals(init);
    });
  }, [kind]);

  const compute = async () => {
    setBusy(true);
    try {
      const r = await bcCalculatorCompute(kind, { kind, assumptions: vals, consent: false });
      setResult(r.result);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (meta) compute(); /* auto compute once meta loaded */ }, [meta, vals]); // eslint-disable-line

  if (!meta) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="pt-32 text-center text-slate-500">Loading…</div></div>;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid={`bc-calc-detail-${kind}`}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center/calculators" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Calculators</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{meta.label}</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">{meta.description}</p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="bc-calc-inputs">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Assumptions (editable)</div>
              <div className="mt-4 space-y-3">
                {meta.assumptions.map((a) => (
                  <div key={a.key}>
                    <label className="flex items-center justify-between text-sm text-slate-700 mb-1">
                      <span>{a.label}</span>
                      <span className="text-xs text-slate-500">{a.unit}</span>
                    </label>
                    <input
                      data-testid={`bc-calc-input-${a.key}`}
                      type="number"
                      value={vals[a.key] ?? a.default}
                      onChange={(e) => setVals({ ...vals, [a.key]: Number(e.target.value) })}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {meta.formula_summary}
              </div>
            </div>

            {/* Results */}
            <div className="space-y-3" data-testid="bc-calc-results">
              {result && meta.scenarios.map((s) => {
                const r = result[s.key];
                if (!r) return null;
                const highlight = s.key === "expected";
                return (
                  <div key={s.key} className={`rounded-2xl border p-5 ${highlight ? "bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white border-transparent" : "bg-white border-slate-200 text-slate-900"}`} data-testid={`bc-calc-scenario-${s.key}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-xs uppercase tracking-widest font-bold ${highlight ? "opacity-80" : "text-slate-500"}`}>{s.label}</div>
                        <div className={`mt-1 text-3xl md:text-4xl font-black tabular-nums ${r.net_monthly >= 0 ? (highlight ? "" : "text-emerald-700") : "text-rose-700"}`}>
                          ${(r.net_monthly || 0).toLocaleString()}
                        </div>
                        <div className={`text-xs ${highlight ? "opacity-80" : "text-slate-500"}`}>net per month</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs uppercase tracking-widest font-bold ${highlight ? "opacity-80" : "text-slate-500"}`}>Annual</div>
                        <div className={`text-2xl font-bold tabular-nums ${r.annual_net >= 0 ? (highlight ? "" : "text-slate-900") : "text-rose-700"}`}>
                          ${(r.annual_net || 0).toLocaleString()}
                        </div>
                        <div className={`text-xs ${highlight ? "opacity-80" : "text-slate-500"}`}>ROI {r.roi_pct_monthly}% / mo</div>
                      </div>
                    </div>
                    <div className={`mt-3 grid grid-cols-2 gap-2 text-xs ${highlight ? "opacity-90" : "text-slate-600"}`}>
                      {Object.entries(r).filter(([k]) => !["net_monthly", "annual_net", "roi_pct_monthly", "platform_cost_monthly"].includes(k)).slice(0, 4).map(([k, v]) => (
                        <div key={k}><span className="uppercase tracking-widest font-bold">{k.replaceAll("_", " ")}: </span>{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {result?._formula && (
                <details className="bg-slate-100 rounded-xl p-3 text-xs text-slate-700">
                  <summary className="cursor-pointer font-semibold text-slate-900">Full formula</summary>
                  <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px]">{result._formula}</pre>
                </details>
              )}
              <div className="text-[11px] text-slate-500 border-t border-slate-200 pt-3">
                <strong>Disclaimer:</strong> Estimates only. No guaranteed savings or revenue outcomes are implied.
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
